import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Barcode, CheckCircle2, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/ui/password-input";

const CREDS = [["client_id", "Client ID"], ["client_secret", "Client Secret"], ["dev_app_key", "Developer Application Key (gw-dev-app-key)"]];
const FIELDS = [["convenio", "Número do convênio de cobrança"], ["carteira", "Carteira"], ["variacao", "Variação da carteira"]];

export default function BoletoSettingsCard() {
  const [s, setS] = useState(null);
  const [creds, setCreds] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/boleto/settings").then((r) => { setS(r.data.settings); setCreds(r.data.has_credentials); }).catch(() => {}); }, []);
  if (!s) return null;

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/boleto/settings", s);
      setS(r.data.settings); setCreds(r.data.has_credentials);
      toast.success("Dados do boleto salvos");
    }
    catch (e) { toast.error(e?.response?.data?.detail || "Erro ao salvar"); }
    finally { setSaving(false); }
  };

  return (
    <section className="bg-white rounded-2xl border border-slate-200 p-5 card-shadow space-y-4" data-testid="boleto-settings-card">
      <div>
        <h2 className="font-heading font-black text-lg flex items-center gap-2"><Barcode className="w-5 h-5 text-[#1e3a8a]" /> Boleto (Banco do Brasil)</h2>
        <p className="text-xs text-slate-500 mt-1">Boleto com Pix, sem juros e sem multa. Gerado ao emitir a NFS-e escolhendo "Boleto".</p>
      </div>
      <p className={`text-xs font-semibold flex items-center gap-1.5 ${creds ? "text-emerald-700" : "text-amber-700"}`} data-testid="boleto-creds-status">
        {creds ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
        {creds ? "Credenciais do banco cadastradas" : "Aguardando as credenciais do Banco do Brasil"}
      </p>
      <div className="grid grid-cols-2 gap-2" data-testid="boleto-ambiente">
        {[["homologacao", "Testes (homologação)"], ["producao", "Produção (boleto real)"]].map(([k, l]) => (
          <button key={k} type="button" onClick={() => setS({ ...s, ambiente: k })} data-testid={`boleto-ambiente-${k}`}
            className={`rounded-lg border-2 py-2 text-xs font-bold ${s.ambiente === k ? (k === "producao" ? "border-emerald-500 bg-emerald-50 text-emerald-800" : "border-amber-400 bg-amber-50 text-amber-800") : "border-slate-200 text-slate-500"}`}>{l}</button>
        ))}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {FIELDS.map(([k, label]) => (
          <div key={k} className="space-y-1">
            <Label className="text-xs font-semibold text-slate-600">{label}</Label>
            <Input inputMode="numeric" value={s[k] ?? ""} onChange={(e) => setS({ ...s, [k]: e.target.value })} data-testid={`boleto-field-${k}`} />
          </div>
        ))}
      </div>
      <div className="rounded-xl border border-slate-200 p-3 space-y-2" data-testid="boleto-creds-box">
        <p className="text-xs font-bold text-slate-700">Credenciais da aplicação (Portal Developers BB)</p>
        {CREDS.map(([k, label]) => (
          <div key={k} className="space-y-1">
            <Label className="text-xs font-semibold text-slate-600">{label}</Label>
            <PasswordInput value={s[k] || ""} onChange={(e) => setS({ ...s, [k]: e.target.value })} placeholder={creds ? "•••••• já salvo (preencha só para trocar)" : ""} autoComplete="off" data-testid={`boleto-cred-${k}`} />
          </div>
        ))}
        <p className="text-[10.5px] text-slate-500">Ficam criptografadas no servidor e nunca aparecem na tela. Ao passar para produção, cole aqui as credenciais de produção.</p>
      </div>
      {s.ambiente === "homologacao" && <p className="text-[10.5px] text-slate-500">Em testes o banco usa o convênio 3128557, carteira 17, variação 35. Na produção, use os números do seu contrato de cobrança.</p>}
      <Button onClick={save} disabled={saving} className="bg-[#1e3a8a] hover:bg-[#1e40af] text-white" data-testid="boleto-settings-save">{saving ? "Salvando..." : "Salvar dados do boleto"}</Button>
    </section>
  );
}
